import { useQuery } from "@tanstack/react-query";
import { supabaseClient } from "@/lib/supabase";

export interface BillingData {
    billing_id: string;
    user_id: string;
    stripe_customer_id: string | null;
    stripe_subscription_id: string | null;
    subscription_plan: "trial" | "starter" | "pro" | "enterprise" | null;
    subscription_status: "active" | "canceled" | "past_due" | "unpaid" | null;
    cancel_at_period_end: boolean;
    current_period_end: string | null;
    created_at: string;
    updated_at: string;
}

export const billingQueryKey = (userId: string) => ["billing", userId] as const;

async function fetchBillingData(userId: string): Promise<BillingData | null> {
    const { data, error } = await supabaseClient
        .from("billing")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1);

    if (error) {
        throw error;
    }

    if (!data || data.length === 0) {
        return null;
    }

    if (data.length > 1) {
        console.warn(`Multiple billing records found for user ${userId}. Using most recent.`);
    }

    return data[0] as BillingData;
}

export function useBilling(userId: string | undefined) {
    return useQuery({
        queryKey: billingQueryKey(userId ?? ""),
        queryFn: () => fetchBillingData(userId!),
        enabled: Boolean(userId),
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 1,
    });
}
